import React, { useCallback, useEffect, useState } from 'react'
import apiClient from '../../config/api'
import Swal from 'sweetalert2'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
})

const formatDateTime = (value) => {
  if (!value) {
    return 'N/A'
  }

  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

const RecentReceipts = () => {
  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true)
      setErrorMessage('')

      const response = await apiClient.get('/api/sales/')
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      
      setSales(data)
    } catch (error) {
      console.error('Error fetching sales:', error)
      setErrorMessage(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          'Failed to load sales. Please try again.'
      )
      setSales([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSales()
  }, [fetchSales])

  const toggleRow = (saleId) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(saleId)) {
      newExpanded.delete(saleId)
    } else {
      newExpanded.add(saleId)
    }
    setExpandedRows(newExpanded)
  }

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-success'
      case 'pending':
        return 'bg-warning'
      case 'cancelled':
        return 'bg-danger'
      default:
        return 'bg-secondary'
    }
  }

  const getPaymentTypeBadgeClass = (type) => {
    switch (type?.toLowerCase()) {
      case 'cash':
        return 'bg-primary'
      case 'card':
        return 'bg-info'
      case 'mobile':
        return 'bg-success'
      default:
        return 'bg-secondary'
    }
  }

  // Filter sales based on search term and date range
  const filteredSales = sales.filter((sale) => {
    // Date range filter
    if (dateFrom || dateTo) {
      const saleDate = new Date(sale.created_at)
      saleDate.setHours(0, 0, 0, 0) // Reset time to start of day
      
      if (dateFrom) {
        const fromDate = new Date(dateFrom)
        fromDate.setHours(0, 0, 0, 0)
        if (saleDate < fromDate) return false
      }
      
      if (dateTo) {
        const toDate = new Date(dateTo)
        toDate.setHours(23, 59, 59, 999) // End of day
        if (saleDate > toDate) return false
      }
    }
    
    // Search term filter
    if (!searchTerm.trim()) return true
    const searchLower = searchTerm.toLowerCase().trim()
    
    // Search in main sale fields
    const matchesMainFields = (
      sale.sale_number?.toLowerCase().includes(searchLower) ||
      sale.branch_name?.toLowerCase().includes(searchLower) ||
      sale.type_of_payment?.toLowerCase().includes(searchLower) ||
      sale.status?.toLowerCase().includes(searchLower) ||
      formatDateTime(sale.created_at).toLowerCase().includes(searchLower) ||
      String(sale.net_amount || '').includes(searchLower) ||
      String(sale.total_amount || '').includes(searchLower)
    )
    
    // Search in sale items (product names, SKUs)
    const matchesItems = sale.items?.some((item) => {
      return (
        item.product_name?.toLowerCase().includes(searchLower) ||
        item.product_sku?.toLowerCase().includes(searchLower) ||
        String(item.quantity || '').includes(searchLower)
      )
    })
    
    return matchesMainFields || matchesItems
  })

  // Calculate total sales
  const totalSales = filteredSales.reduce((sum, sale) => {
    return sum + (Number(sale.net_amount) || 0)
  }, 0)

  // Calculate stats
  const totalSalesCount = filteredSales.length
  const completedSales = filteredSales.filter(s => s.status?.toLowerCase() === 'completed').length
  const pendingSales = filteredSales.filter(s => s.status?.toLowerCase() === 'pending').length
  const cancelledSales = filteredSales.filter(s => s.status?.toLowerCase() === 'cancelled').length

  const handleReturn = async (sale) => {
    // Check if device is online
    if (!navigator.onLine) {
      Swal.fire({
        icon: 'warning',
        title: 'Connection Required',
        text: 'This process requires an internet connection. Please check your connection and try again.',
        confirmButtonText: 'OK'
      })
      return
    }

    // Check if sale has items
    if (!sale.items || sale.items.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items',
        text: 'This sale has no items to return.'
      })
      return
    }

    // Build product options for dropdown
    const productOptions = sale.items.map((item, index) => {
      const productName = item.product_name || 'N/A'
      const productSku = item.product_sku || 'N/A'
      const maxQuantity = parseFloat(item.quantity) || 0
      // Use product field (which contains the product ID) or fallback to product_id
      const productId = item.product || item.product_id
      if (!productId) {
        console.warn('Product ID not found for item:', item)
        console.warn('Available item fields:', Object.keys(item))
        return ''
      }
      return `<option value="${productId}" data-max-qty="${maxQuantity}" data-index="${index}">${productName} (SKU: ${productSku}) - Qty: ${maxQuantity}</option>`
    }).filter(option => option !== '').join('')

    // Check if we have any valid product options
    if (!productOptions || productOptions.trim() === '') {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No valid products found in this sale. Please contact support.'
      })
      return
    }

    const { value: formValues } = await Swal.fire({
      title: 'Process Return',
      html: `
        <input id="swal-auth-code" class="swal2-input" placeholder="Authorization Code" required>
        <select id="swal-product" class="swal2-input" required>
          <option value="">Select Product</option>
          ${productOptions}
        </select>
        <input id="swal-quantity" class="swal2-input" type="number" placeholder="Quantity" step="0.01" min="0.01" required>
        <textarea id="swal-reason" class="swal2-input" placeholder="Reason (optional)" style="resize: vertical; min-height: 80px;"></textarea>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Process Return',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      preConfirm: () => {
        const authCode = document.getElementById('swal-auth-code').value.trim()
        const productId = document.getElementById('swal-product').value
        const quantity = parseFloat(document.getElementById('swal-quantity').value)
        const reason = document.getElementById('swal-reason').value.trim()

        if (!authCode) {
          Swal.showValidationMessage('Authorization code is required')
          return false
        }

        if (!productId) {
          Swal.showValidationMessage('Please select a product')
          return false
        }

        const parsedProductId = parseInt(productId)
        if (isNaN(parsedProductId) || parsedProductId <= 0) {
          Swal.showValidationMessage('Invalid product selected')
          return false
        }

        if (isNaN(quantity) || quantity <= 0) {
          Swal.showValidationMessage('Quantity must be greater than zero')
          return false
        }

        // Get max quantity for selected product
        const selectedOption = document.getElementById('swal-product').options[document.getElementById('swal-product').selectedIndex]
        const maxQuantity = parseFloat(selectedOption.getAttribute('data-max-qty')) || 0

        if (quantity > maxQuantity) {
          Swal.showValidationMessage(`Quantity cannot exceed ${maxQuantity} (available quantity)`)
          return false
        }

        return {
          authorization_code: authCode,
          product_id: parsedProductId,
          quantity: quantity.toString(),
          reason: reason || ''
        }
      },
      didOpen: () => {
        document.getElementById('swal-auth-code').focus()
        
        // Update max quantity when product selection changes
        const productSelect = document.getElementById('swal-product')
        const quantityInput = document.getElementById('swal-quantity')
        
        productSelect.addEventListener('change', () => {
          const selectedOption = productSelect.options[productSelect.selectedIndex]
          const maxQuantity = parseFloat(selectedOption.getAttribute('data-max-qty')) || 0
          quantityInput.setAttribute('max', maxQuantity)
          quantityInput.value = ''
        })
      }
    })

    if (formValues) {
      // Double-check connection before making API call
      if (!navigator.onLine) {
        Swal.fire({
          icon: 'warning',
          title: 'Connection Lost',
          text: 'Your connection was lost. This process requires an internet connection. Please check your connection and try again.',
          confirmButtonText: 'OK'
        })
        return
      }

      try {
        Swal.fire({
          title: 'Processing...',
          text: 'Please wait while we process the return',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading()
          }
        })

        // Log the payload for debugging
        console.log('Return payload:', formValues)
        console.log('Product ID type:', typeof formValues.product_id, 'Value:', formValues.product_id)
        
        await apiClient.post(`/api/sales/${sale.id}/returns/`, formValues)

        Swal.fire({
          icon: 'success',
          title: 'Return Processed',
          text: 'The return has been processed successfully!',
          timer: 2000,
          showConfirmButton: false
        })

        // Refresh sales list
        fetchSales()
      } catch (error) {
        console.error('Error processing return:', error)
        
        // Check if it's a network error
        if (!navigator.onLine || error.message === 'Network Error' || !error.response) {
          Swal.fire({
            icon: 'warning',
            title: 'Connection Error',
            text: 'This process requires an internet connection. Please check your connection and try again.',
            confirmButtonText: 'OK'
          })
          return
        }
        
        const errorMessage = error.response?.data?.detail ||
                            error.response?.data?.message ||
                            error.response?.data?.authorization_code?.[0] ||
                            error.response?.data?.product_id?.[0] ||
                            error.response?.data?.quantity?.[0] ||
                            'Failed to process return. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Recent Receipts</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Receipts</li>
            <li className="breadcrumb-item active">Recent Receipts</li>
          </ol>
        </nav>
      </div>{/* End Page Title */}
      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {/* Header with Stats */}
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ 
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-receipt-cutoff me-2"></i>
                      Recent Receipts
                    </h5>
                    
                    {/* Stats Badges */}
                    {!loading && sales.length > 0 && (
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}>
                          <i className="bi bi-receipt me-2"></i>
                          <span>Total: <strong>{totalSalesCount}</strong></span>
                        </div>
                        
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)'
                        }}>
                          <i className="bi bi-check-circle me-2"></i>
                          <span>Completed: <strong>{completedSales}</strong></span>
                        </div>
                        
                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)'
                        }}>
                          <i className="bi bi-clock me-2"></i>
                          <span>Pending: <strong>{pendingSales}</strong></span>
                        </div>

                        {cancelledSales > 0 && (
                          <div className="d-flex align-items-center" style={{
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            color: 'white',
                            fontSize: '13px',
                            fontWeight: '600',
                            boxShadow: '0 2px 8px rgba(250, 112, 154, 0.3)'
                          }}>
                            <i className="bi bi-x-circle me-2"></i>
                            <span>Cancelled: <strong>{cancelledSales}</strong></span>
                          </div>
                        )}

                        <div className="d-flex align-items-center" style={{
                          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(79, 172, 254, 0.3)'
                        }}>
                          <i className="bi bi-currency-dollar me-2"></i>
                          <span>Total: <strong>{currencyFormatter.format(totalSales)}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Search Filter */}
                {!loading && (
                  <div className="row mb-3 align-items-center">
                    <div className="col-md-4">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search sales..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-calendar-event"></i>
                        </span>
                        <input
                          type="date"
                          className="form-control"
                          placeholder="From Date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-calendar-check"></i>
                        </span>
                        <input
                          type="date"
                          className="form-control"
                          placeholder="To Date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          min={dateFrom || undefined}
                        />
                      </div>
                    </div>
                    <div className="col-md-2 d-flex justify-content-end gap-2">
                      {(dateFrom || dateTo || searchTerm) && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            setDateFrom('')
                            setDateTo('')
                            setSearchTerm('')
                          }}
                          title="Clear all filters"
                        >
                          <i className="bi bi-x-circle me-1"></i>
                          Clear
                        </button>
                      )}
                    </div>
                    {(searchTerm || dateFrom || dateTo) && (
                      <div className="col-12 mt-2">
                        <small className="text-muted">
                          <i className="bi bi-funnel me-1"></i>
                          {filteredSales.length} {filteredSales.length === 1 ? 'sale' : 'sales'} found
                        </small>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Sales Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading sales...</p>
                  </div>
                ) : errorMessage ? (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errorMessage}
                    <button
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={fetchSales}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Retry
                    </button>
                  </div>
                ) : sales.length === 0 ? (
                  <div className="alert alert-info" role="alert">
                    <i className="bi bi-info-circle me-2"></i>
                    No sales found.
                  </div>
                ) : filteredSales.length === 0 ? (
                  <div className="alert alert-warning" role="alert">
                    <i className="bi bi-search me-2"></i>
                    No sales match your search criteria. Try a different search term.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}></th>
                          <th>Sale Number</th>
                          <th>Date</th>
                          <th className="text-end">Net Amount</th>
                          <th>Type of Payment</th>
                          <th>Status</th>
                          <th style={{ width: '100px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSales.map((sale) => (
                          <React.Fragment key={sale.id}>
                            <tr
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleRow(sale.id)}
                            >
                              <td>
                                <i
                                  className={`bi bi-chevron-${expandedRows.has(sale.id) ? 'down' : 'right'}`}
                                ></i>
                              </td>
                              <td>
                                <strong>{sale.sale_number}</strong>
                              </td>
                              <td>
                                <small>{formatDateTime(sale.created_at)}</small>
                              </td>
                              <td className="text-end">
                                <strong>
                                  {currencyFormatter.format(Number(sale.net_amount) || 0)}
                                </strong>
                              </td>
                              <td>
                                <span
                                  className={`badge ${getPaymentTypeBadgeClass(sale.type_of_payment)}`}
                                >
                                  {sale.type_of_payment || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${getStatusBadgeClass(sale.status)}`}
                                >
                                  {sale.status || 'N/A'}
                                </span>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleReturn(sale)
                                  }}
                                  title="Return Sale"
                                >
                                  <i className="bi bi-arrow-return-left"></i>
                                </button>
                              </td>
                            </tr>
                            {expandedRows.has(sale.id) && (
                              <tr>
                                <td colSpan="7" className="bg-light">
                                  <div className="p-4">
                                    <h6 className="mb-3">Sale Details</h6>
                                    <div className="row mb-3">
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Sale Number</small>
                                        <p className="mb-0">
                                          <strong>{sale.sale_number}</strong>
                                        </p>
                                      </div>
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Branch</small>
                                        <p className="mb-0">{sale.branch_name || 'N/A'}</p>
                                      </div>
                                     
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Date</small>
                                        <p className="mb-0">
                                          <small>{formatDateTime(sale.created_at)}</small>
                                        </p>
                                      </div>
                                    </div>
                                    <div className="row mb-3">
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Total Amount</small>
                                        <p className="mb-0">
                                          <strong>
                                            {currencyFormatter.format(Number(sale.total_amount) || 0)}
                                          </strong>
                                        </p>
                                      </div>
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Discount</small>
                                        <p className="mb-0">
                                          {currencyFormatter.format(Number(sale.discount) || 0)}
                                        </p>
                                      </div>
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Tax</small>
                                        <p className="mb-0">
                                          {currencyFormatter.format(Number(sale.tax) || 0)}
                                        </p>
                                      </div>
                                      <div className="col-md-3">
                                        <small className="text-muted d-block">Net Amount</small>
                                        <p className="mb-0">
                                          <strong>
                                            {currencyFormatter.format(Number(sale.net_amount) || 0)}
                                          </strong>
                                        </p>
                                      </div>
                                    </div>
                                    {sale.notes && (
                                      <div className="mb-3">
                                        <small className="text-muted d-block">Notes</small>
                                        <p className="mb-0">{sale.notes}</p>
                                      </div>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                      <h6 className="mb-0">Sale Items</h6>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleReturn(sale)
                                        }}
                                      >
                                        <i className="bi bi-arrow-return-left me-1"></i>
                                        Return
                                      </button>
                                    </div>
                                    {sale.items && sale.items.length > 0 ? (
                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                          <thead className="table-secondary">
                                            <tr>
                                              <th>Product Name</th>
                                              <th>SKU</th>
                                              <th className="text-end">Quantity</th>
                                              <th className="text-end">Unit Price</th>
                                              <th className="text-end">Discount</th>
                                              <th className="text-end">Subtotal</th>
                                              <th className="text-end">Profit</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sale.items.map((item) => (
                                              <tr key={item.id}>
                                                <td>{item.product_name || 'N/A'}</td>
                                                <td>
                                                  <code>{item.product_sku || 'N/A'}</code>
                                                </td>
                                                <td className="text-end">{item.quantity || '0.00'}</td>
                                                <td className="text-end">
                                                  {currencyFormatter.format(Number(item.unit_price) || 0)}
                                                </td>
                                                <td className="text-end">
                                                  {currencyFormatter.format(Number(item.discount) || 0)}
                                                </td>
                                                <td className="text-end">
                                                  <strong>
                                                    {currencyFormatter.format(Number(item.subtotal) || 0)}
                                                  </strong>
                                                </td>
                                                <td className="text-end">
                                                  {currencyFormatter.format(Number(item.profit) || 0)}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <p className="text-muted">No items found</p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default RecentReceipts

